<template>
  <PsTabs
    v-model="currTabId"
    :tabs="permittedTabs"
    class="task-panel-tabs"
  >
    <template #suffix>
      <PsButton
        class="close"
        :circle="true"
        :border="false"
        size="sm"
        @click="emit('close')"
      >
        <PsIcon
          name="close"
          size="14"
        />
      </PsButton>
    </template>
    <template #default="{ tab }">
      <Component
        :is="tab?.component"
        v-if="tab?.component"
        v-loading="isLoading"
        @open="open"
        @pick="pick(0, $event)"
      />
      <div v-else>
        Вкладка "{{ currentTabName }}"
      </div>
    </template>
  </PsTabs>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useAsyncState } from '@vueuse/core'
import { vLoading } from 'element-plus'
import { noop } from 'lodash-es'
import PsButton from '@/ContextTab/components/UI/Button/PsButton.vue'
import PsIcon from '@/ContextTab/components/UI/PsIcon.vue'
import PsTabs from '@/ContextTab/components/UI/PsTabs.vue'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { tabs } from '@/ContextTab/modules/news/tasks-panel/components/nav/tabs'
import { useNewsEditorStore } from '@/stores/newsEditor.ts'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import type { TaskPanelSetType } from './nav/types'


const currTabId = defineModel<TaskPanelSetType>({ required: true })

defineProps<{
  modelValue: TaskPanelSetType
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const { allowedTaskPanels } = useAccess()

const permittedTabs = computed(() => tabs
  .filter((tab) => allowedTaskPanels.value.includes(tab.id)),
)

const currentTabName = computed(() => {
  const curTab = tabs.find((tab) => tab.id === currTabId.value)
  return curTab
    ? curTab.label
    : ''
})

const { open, pick: pickPromise, openByNewsItem } = useNewsEditorStore()

const { isLoading, execute: pick } = useAsyncState(
  pickPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)
</script>

<style>
.task-panel-tabs {
  --tabs--header--background: #fff6e6;
  --tabs--header--border-radius: 0;
  --tabs--border-radius: 0;
  --tabs--tab--padding: 0;
  --tabs--padding-top: 1rem;
  --tabs--header--border-bottom-color: var(--color-gray);
  --tabs--header--border-bottom-width: 1px;
  --tabs--header--margin-bottom: 0;

  &.tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    max-height: 100vh;
  }

  &.tabs .tabs-header {
    padding: calc(0.5rem + 1px) 1rem;
    border-bottom: none;
    overflow: visible;
  }

  .tab {
    overflow-y: hidden;
    height: 100%;
  }

  & .tab-body {
    height: 100%;
  }

  .close.-transparent:not(:disabled, .-disabled):hover {
    color: #bd985c;
    background-color: #f3e4ca;
  }
}
</style>
